import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';
import { AnimateOnScroll } from './AnimateOnScroll';

const CTASection = () => {
  const navigate = useNavigate();
  const { t, tm } = useLanguage();
  const actionCopy = tm('home.cta.actions');

  const quickActions = useMemo(
    () => [
      {
        id: 1,
        icon: 'Search',
        title: actionCopy[0]?.title,
        description: actionCopy[0]?.description,
        action: () => navigate('/job-marketplace'),
        color: 'text-amber-400',
        bgColor: 'bg-amber-500/10 border-amber-500/20',
      },
      {
        id: 2,
        icon: 'Users',
        title: actionCopy[1]?.title,
        description: actionCopy[1]?.description,
        action: () => navigate('/service-portfolio-hub'),
        color: 'text-amber-400',
        bgColor: 'bg-amber-500/10 border-amber-500/20',
      },
      {
        id: 3,
        icon: 'Globe',
        title: actionCopy[2]?.title,
        description: actionCopy[2]?.description,
        action: () => navigate('/global-trade-network'),
        color: 'text-blue-400',
        bgColor: 'bg-blue-500/10 border-blue-500/20',
      },
      {
        id: 4,
        icon: 'MessageSquare',
        title: actionCopy[3]?.title,
        description: actionCopy[3]?.description,
        action: () => navigate('/contact'),
        color: 'text-indigo-400',
        bgColor: 'bg-indigo-500/10 border-indigo-500/20',
      },
    ],
    [actionCopy, navigate]
  );

  return (
    <section className="bg-gradient-to-br from-[#060D21] via-[#0A1128] to-[#0D1836] py-16 md:py-24 relative overflow-hidden border-t border-slate-800/80 text-white">
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/20 rounded-full blur-[140px]" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600/20 rounded-full blur-[140px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <AnimateOnScroll>
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full mb-3">
              <Icon name="Rocket" size={15} className="text-amber-400" />
              <span className="text-xs md:text-sm font-bold tracking-widest text-amber-400 uppercase">
                START A CONVERSATION
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight">
              {t('home.cta.title')}
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-slate-300 max-w-3xl mx-auto mb-8 font-normal">
              {t('home.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                onClick={() => navigate('/contact')}
                className="px-8 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-extrabold shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:-translate-y-0.5 border-0"
                iconName="Rocket"
                iconPosition="left"
              >
                {t('home.cta.startPartnership')}
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/client-portal')}
                className="px-8 py-3.5 rounded-xl bg-white/[0.05] hover:bg-white/[0.12] text-white border border-white/20 hover:border-amber-400/40 backdrop-blur-md hover:-translate-y-0.5"
                iconName="LogIn"
                iconPosition="left"
              >
                {t('home.cta.clientPortal')}
              </Button>
            </div>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action, index) => (
            <AnimateOnScroll key={action.id} delay={index * 100}>
              <button
                type="button"
                onClick={action.action}
                className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-slate-800 hover:border-amber-500/40 hover:bg-slate-900/90 transition-all duration-300 group text-left flex flex-col justify-between w-full h-full shadow-xl"
              >
                <div>
                  <div
                    className={`w-12 h-12 md:w-14 md:h-14 rounded-xl ${action.bgColor} border flex items-center justify-center mb-5 group-hover:scale-105 transition-transform`}
                  >
                    <Icon name={action.icon} size={24} className={action.color} />
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-white mb-2">{action.title}</h3>
                  <p className="text-xs md:text-sm text-slate-300 font-normal leading-relaxed">{action.description}</p>
                </div>
                <div className="flex items-center gap-2 mt-6 text-amber-400 font-semibold text-xs md:text-sm">
                  <span>{t('home.cta.getStarted')}</span>
                  <Icon
                    name="ArrowRight"
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </div>
              </button>
            </AnimateOnScroll>
          ))}
        </div>

        <AnimateOnScroll delay={400}>
          <div className="mt-12 md:mt-16 bg-slate-900/70 backdrop-blur-xl rounded-2xl p-8 md:p-12 border border-slate-800">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-slate-800">
              <div className="pt-4 md:pt-0">
                <Icon name="Shield" size={32} className="text-amber-400 mx-auto mb-3" />
                <h4 className="text-base md:text-lg font-bold text-white mb-2">
                  {t('home.cta.isoTitle')}
                </h4>
                <p className="text-xs md:text-sm text-slate-300">{t('home.cta.isoDesc')}</p>
              </div>
              <div className="pt-6 md:pt-0">
                <Icon name="Clock" size={32} className="text-amber-400 mx-auto mb-3" />
                <h4 className="text-base md:text-lg font-bold text-white mb-2">
                  {t('home.cta.supportTitle')}
                </h4>
                <p className="text-xs md:text-sm text-slate-300">{t('home.cta.supportDesc')}</p>
              </div>
              <div className="pt-6 md:pt-0">
                <Icon name="Award" size={32} className="text-amber-400 mx-auto mb-3" />
                <h4 className="text-base md:text-lg font-bold text-white mb-2">
                  {t('home.cta.recognitionTitle')}
                </h4>
                <p className="text-xs md:text-sm text-slate-300">{t('home.cta.recognitionDesc')}</p>
              </div>
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default CTASection;
